import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, ArrowRight, Check, CheckCheck, Gamepad2, Percent, RotateCw, ShoppingBag, Sparkles, Users, Wallet } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import './halloween-event.css';

const taskIcons = { game: Gamepad2, wallet: Wallet, users: Users };
const taskHelp = {
  'play-games': 'Successful Main Wallet loads count, in the same game or different games. Collect once per week.',
  'play-amount': 'Successful Main Wallet game loads count toward this total. Collect once per week.',
  'deposit-one': 'Make a successful wallet deposit this week. Collect once per week.',
  'deposit-three': 'Make three successful wallet deposits this week. Collect once per week.',
  'refer-one': 'Refer a new player who signs up and makes a successful deposit this week. Collect once per week.',
  'refer-five': 'Refer five new players who sign up and make a successful deposit this week. Collect once per week.',
};
const rewardIcons = { fp: Sparkles, wallet: Wallet, deposit_bonus: Percent, roulette_ticket: RotateCw };
const detail = error => typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'Could not confirm the result. Check the result or retry the same request.';
const readIntent = storageKey => {
  const raw = localStorage.getItem(storageKey);
  let saved;
  try { saved = JSON.parse(raw || 'null'); } catch { return null; }
  return saved && /^[a-zA-Z0-9_-]{16,100}$/.test(saved.key) && typeof saved.rewardId === 'string' ? saved : null;
};
const exchangeKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // randomUUID is missing in older mobile browsers; Web Crypto still supplies
  // a collision-resistant request identity. Never fall back to a weak random ID.
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};
function Pumpkin({ className = '' }) { return <img className={`h-event__pumpkin ${className}`} src="/halloween-preview/pumpkin-collectible-v1.png" alt="" aria-hidden="true" />; }

export default function HalloweenEventPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const home = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const storageKey = `halloween-intent:halloween-2026:${user?.tenant_id}:${user?.id}`;
  const [state, setState] = useState(null);
  const [message, setMessage] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [market, setMarket] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const epoch = useRef(0);
  const titleRef = useRef(null);
  const confirmRef = useRef(null);
  const announcementRef = useRef(null);
  const rewardButtons = useRef({});
  const selectionReturn = useRef(null);
  const marketTrigger = useRef(null);
  const taskReturn = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    document.documentElement.classList.add('halloween-event-active');
    return () => { mounted.current = false; epoch.current += 1; document.documentElement.classList.remove('halloween-event-active'); };
  }, []);
  const refresh = useCallback(async () => {
    const requestEpoch = ++epoch.current;
    try {
      const { data } = await api.get('/api/halloween/state', { timeout: 15000 });
      if (mounted.current && epoch.current === requestEpoch) { setState(data); setLoadError(false); }
    } catch (error) {
      if (mounted.current && epoch.current === requestEpoch) { setLoadError(true); setMessage(detail(error)); }
    }
  }, []);
  useEffect(() => {
    setState(null);
    try {
      const saved = readIntent(storageKey);
      if (saved) {
        setPending(saved); setMarket(true); setMessage('An exchange needs confirmation. Check its result before starting another.');
      } else setPending(null);
    } catch { setPending(null); }
    refresh();
  }, [storageKey, refresh]);
  useEffect(() => {
    if (taskReturn.current && !market) { marketTrigger.current?.focus(); taskReturn.current = false; }
    else { window.scrollTo(0, 0); titleRef.current?.focus({ preventScroll: true }); }
  }, [market]);
  useEffect(() => {
    if (selected) { confirmRef.current?.focus({ preventScroll: true }); confirmRef.current?.scrollIntoView?.({ block: 'center', behavior: 'auto' }); }
    else if (selectionReturn.current) { rewardButtons.current[selectionReturn.current]?.focus({ preventScroll: true }); selectionReturn.current = null; }
  }, [selected]);
  useEffect(() => {
    if (message) announcementRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
  }, [message]);
  const start = () => {
    if (busyRef.current) return false;
    busyRef.current = true; setBusy(true); epoch.current += 1; return true;
  };
  const finish = () => { busyRef.current = false; if (mounted.current) setBusy(false); };
  const closeSelection = () => { selectionReturn.current = selected?.id; setSelected(null); };
  const back = () => {
    if (market) { taskReturn.current = true; setSelected(null); setMarket(false); }
    else navigate(home);
  };
  const collect = async task => {
    if (!start()) return;
    try {
      const { data } = await api.post(`/api/halloween/tasks/${task.id}/claim`, { cycle: state.cycle }, { timeout: 15000 });
      if (data?.status !== 'completed' || data?.task_id !== task.id) throw new Error('No committed claim receipt');
      setMessage(`${data.pumpkins} pumpkins collected.`);
      await refresh();
    } catch (error) { setMessage(detail(error)); await refresh(); }
    finally { finish(); }
  };
  const committed = async (receipt, intent) => {
    if (receipt?.status !== 'completed' || receipt?.request_key !== intent.key || receipt?.reward_id !== intent.rewardId) throw new Error('No matching committed exchange receipt');
    let cleared = true;
    try { localStorage.removeItem(storageKey); } catch { cleared = false; }
    setPending(cleared ? null : intent); setSelected(null);
    const result = receipt.type === 'deposit_bonus' ? `${receipt.title} saved for one eligible next deposit.` : `${receipt.title} received.`;
    setMessage(cleared ? result : `${result} Your browser could not clear the saved request. Enable site storage and check the result before another exchange.`);
    await refresh();
  };
  const checkResult = async () => {
    if (!pending || !start()) return;
    try {
      const { data } = await api.get(`/api/halloween/exchanges/${pending.key}`, { timeout: 15000 });
      await committed(data, pending);
    } catch (error) {
      setMessage(error?.response?.status === 404 ? 'No committed exchange found. Retry this same exchange; your request ID will not change.' : detail(error));
    } finally { finish(); }
  };
  const exchange = async () => {
    if ((!selected && !pending) || !start()) return;
    let intent = pending;
    let sent = false;
    try {
      if (!intent) {
        // Persist before sending; reload/timeout must never generate a second intent.
        const saved = readIntent(storageKey);
        intent = saved || { key: exchangeKey(), rewardId: selected.id };
        localStorage.setItem(storageKey, JSON.stringify(intent));
        if (readIntent(storageKey)?.key !== intent.key) throw new Error('Exchange request was not saved');
        setPending(intent);
      }
      sent = true;
      const { data } = await api.post(`/api/halloween/rewards/${intent.rewardId}/exchange`, { request_key: intent.key }, {
        timeout: 20000, headers: { 'X-Idempotency-Key': intent.key },
      });
      await committed(data, intent);
    } catch (error) {
      setMessage(sent ? detail(error) : 'No exchange was sent. Your browser could not safely save the request. Allow site storage or open this page in an updated Safari or Chrome, then try again.');
      // A definitive rejection plus no committed receipt is safe to dismiss.
      // Network/server/unknown responses retain the original persisted intent.
      if (intent && [400, 403, 404, 409, 422].includes(error?.response?.status)) {
        try {
          const { data } = await api.get(`/api/halloween/exchanges/${intent.key}`, { timeout: 15000 });
          await committed(data, intent);
        } catch (lookup) {
          if (lookup?.response?.status === 404) {
            try { localStorage.removeItem(storageKey); setPending(null); } catch { /* Keep the saved request recoverable. */ }
          }
        }
      }
      await refresh();
    } finally { finish(); }
  };
  const ready = Boolean(state?.active && !loadError);
  return <div className="h-event" data-testid="halloween-event">
    <header className="h-event__header"><div className="h-event__header-inner">
      <button className="h-event__back" type="button" onClick={back} aria-label={market ? 'Back to Halloween tasks' : 'Back to lobby'}><ArrowLeft size={20} /></button>
      <h1 ref={titleRef} tabIndex={-1}>{market ? 'Pumpkin Market' : 'Halloween Event'}</h1>
      <div className="h-event__inventory" aria-label={state ? `${state.pumpkins} pumpkins available` : 'Loading pumpkins'}><Pumpkin /><strong data-testid="pumpkin-balance">{state ? state.pumpkins : '—'}</strong><span>pumpkins</span></div>
    </div></header>
    <main className="h-event__main" aria-busy={busy}>
      <div ref={announcementRef} className="h-event__announcement" role="status" aria-live="polite">{message}</div>
      {(!state || loadError) && <section className="h-event__status"><p>{loadError ? 'Your event data could not be refreshed. Rewards stay locked until it is confirmed.' : 'Loading your Halloween tasks and pumpkins…'}</p><button type="button" className="h-event__button" disabled={busy} onClick={refresh}>Refresh event</button></section>}
      {state && <>
        {!state.active && <p className="h-event__announcement">This event is not live. Previous exchange history remains available.</p>}
        {!market ? <>
          <a className="h-event__artwork" href="#halloween-tasks" aria-label="View Halloween tasks"><img src="/halloween-preview/inside-v1.png" alt="Our Halloween hostess holding a glowing pumpkin lantern" width="1774" height="887" /></a>
          <div className="h-event__intro"><Pumpkin className="h-event__side h-event__side--left" /><div><h2>Complete tasks.<br /><span>Collect pumpkins.</span></h2></div><Pumpkin className="h-event__side h-event__side--right" /></div>
          <section id="halloween-tasks" className="h-event__tasks" aria-labelledby="halloween-task-title">
            <div className="h-event__section-title"><h2 id="halloween-task-title">This week's tasks</h2><span>{state.tasks.filter(t => t.collected).length}/{state.tasks.length} collected</span></div>
            <button type="button" className="h-event__button" disabled={busy} onClick={refresh}>Refresh progress</button>
            <div className="h-event__task-list">{state.tasks.map(task => {
              const Icon = taskIcons[task.icon] || Wallet;
              const complete = task.progress >= task.goal;
              return <article className={`h-event__task${task.collected ? ' h-event__task--collected' : ''}`} key={task.id} data-testid={`task-${task.id}`}>
                <div className="h-event__task-top"><Icon size={24} aria-hidden="true" /><div><h3>{task.title}</h3>{taskHelp[task.id] && <p>{taskHelp[task.id]}</p>}</div><div className="h-event__reward-count"><Pumpkin /><strong>+{task.pumpkins}</strong></div></div>
                <div className="h-event__task-footer"><div className="h-event__progress"><span>{task.collected ? 'Collected' : complete ? 'Ready to collect' : task.unit === 'USD' ? `$${task.progress.toFixed(2)} / $${task.goal}` : `${task.progress}/${task.goal} complete`}</span><progress max={task.goal} value={task.progress} aria-label={`${task.title} progress`} /></div>
                  <button type="button" className={`h-event__button${complete && !task.collected ? ' h-event__button--primary' : ''}`} disabled={!ready || busy || task.collected || !complete} onClick={() => collect(task)} aria-label={task.collected ? `${task.title} collected` : `Collect ${task.pumpkins} pumpkins from ${task.title}`}>
                    {task.collected ? <><Check size={16} />Collected</> : <>Collect {task.pumpkins}<Pumpkin /></>}
                  </button>
                </div>
              </article>;
            })}</div>
          </section>
          <section className="h-event__market-entry" aria-labelledby="halloween-market-title"><div className="h-event__market-art" aria-hidden="true"><Pumpkin /><ShoppingBag size={42} /><Pumpkin /></div><h2 id="halloween-market-title">Pumpkin Marketplace</h2><p>Exchange your pumpkins for rewards.</p><button ref={marketTrigger} type="button" className="h-event__button h-event__button--primary h-event__button--wide" onClick={() => { setSelected(null); setMarket(true); }}>Enter Halloween Marketplace<ArrowRight size={19} /></button></section>
        </> : <>
          <div className="h-event__intro h-event__intro--market"><Pumpkin className="h-event__side" /><div><h2>Pumpkin Market.<br /><span>Choose your reward.</span></h2><p>You have {state.pumpkins} pumpkins to exchange.</p></div><Pumpkin className="h-event__side" /></div>
          {pending && <section className="h-event__confirmation" aria-label="Recover exchange"><h2>Confirm your previous exchange</h2><p>Your request ID has been saved. Checking or retrying uses the same request and cannot issue this reward twice.</p><div><button type="button" className="h-event__button" disabled={busy} onClick={checkResult}>Check result</button><button type="button" className="h-event__button h-event__button--primary" disabled={busy} onClick={exchange}>Retry same exchange</button></div></section>}
          <section className="h-event__reward-list" aria-label="Halloween marketplace rewards">{state.rewards.map(reward => {
            const Icon = rewardIcons[reward.type] || Sparkles;
            const canAfford = state.pumpkins >= reward.cost;
            const bonusPending = reward.type === 'deposit_bonus' && Boolean(state.pending_bonus);
            return <article key={reward.id} className="h-event__shop-item"><Icon size={38} aria-hidden="true" /><h3>{reward.title}</h3><p>{reward.description}</p><div className="h-event__price"><Pumpkin /><strong>{reward.cost}</strong><span>pumpkins</span></div><button ref={button => { rewardButtons.current[reward.id] = button; }} type="button" className={`h-event__button${canAfford ? ' h-event__button--primary' : ''}`} disabled={!ready || busy || pending || bonusPending || !canAfford} onClick={() => { setMessage(''); setSelected(reward); }} aria-label={`Exchange for ${reward.title}`} aria-expanded={selected?.id === reward.id}>{bonusPending ? 'Bonus already pending' : canAfford ? 'Exchange' : `Need ${reward.cost - state.pumpkins} more`}{canAfford && <ArrowRight size={16} />}</button>
              {selected?.id === reward.id && !pending && <section className="h-event__confirmation h-event__confirmation--inline" aria-label="Confirm exchange"><h2>Exchange for {selected.title}?</h2><p>Spend {selected.cost} pumpkins. You will have {state.pumpkins - selected.cost} left.</p><p className="h-event__hint">The exchange is final once confirmed.</p><div><button ref={confirmRef} type="button" className="h-event__button h-event__button--primary" disabled={!ready || busy || bonusPending || !canAfford} onClick={exchange}>Confirm exchange</button><button type="button" className="h-event__button" disabled={busy} onClick={closeSelection}>Cancel</button></div></section>}
            </article>;
          })}</section>
          {state.tickets_available > 0 && <section className="h-event__confirmation" aria-label="Your roulette tickets"><h2>{state.tickets_available} roulette ticket{state.tickets_available === 1 ? '' : 's'} available</h2><p>Use your tickets with this platform’s configured roulette prizes.</p><div><button type="button" className="h-event__button h-event__button--primary" disabled={busy} onClick={() => navigate(`${home}/explore/roulette?halloween_ticket=1`)}>Use roulette ticket<RotateCw size={16} /></button></div></section>}
          <section className="h-event__exchange-history" aria-labelledby="exchange-history-title"><h2 id="exchange-history-title">Your exchanges</h2>{state.exchanges.length ? <ul>{state.exchanges.map(item => <li key={item.id}><CheckCheck size={20} /><div><strong>{item.title}</strong><span>{item.type === 'deposit_bonus' ? item.bonus_status === 'applied' ? 'Bonus applied to deposit' : 'Saved for one eligible deposit' : 'Reward received'}</span><span>{new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.created_at))} ET</span></div><span>{item.cost} pumpkins</span></li>)}</ul> : <p>No exchanges yet.</p>}</section>
          <button type="button" className="h-event__button h-event__button--wide" onClick={back}><ArrowLeft size={17} />Back to tasks</button>
        </>}
      </>}
    </main>
  </div>;
}
